import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  GripVertical,
  Search,
  MoreVertical,
  Eye,
  EyeOff,
  Filter,
  Settings
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useCustomFields } from '@/hooks/useCustomFields';
import { useAuth } from '@/contexts/AuthContext';
import { CustomFieldDefinition, FIELD_TYPE_CONFIGS } from '@/types/customField';
import { CreateFieldModal } from './CreateFieldModal';

export const CustomFieldManager: React.FC = () => {
  const { user } = useAuth();
  const {
    fields,
    isLoading,
    error,
    createField,
    updateField,
    deleteField,
    reorderFields,
    isCreating,
    isUpdating,
    isDeleting,
  } = useCustomFields();

  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomFieldDefinition | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Filter fields by search
  const filteredFields = fields.filter(field =>
    field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    field.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check permission
  const canManage = user?.role === 'CEO' || user?.role === 'GROUP_MANAGER';

  if (!canManage) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-500">
              You don't have permission to manage custom fields.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newOrder = [...filteredFields];
    const draggedField = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedField);

    // Update local state for smooth drag
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex !== null) {
      // Save new order to server
      const newOrder = filteredFields.map(f => f.id);
      await reorderFields(newOrder);
    }
    setDraggedIndex(null);
  };

  const handleToggleActive = async (field: CustomFieldDefinition) => {
    await updateField(field.id, { isActive: !field.isActive });
  };

  const handleDelete = async (field: CustomFieldDefinition) => {
    if (window.confirm(`Are you sure you want to delete "${field.name}"? This action cannot be undone.`)) {
      await deleteField(field.id);
    }
  };

  const getFieldTypeLabel = (type: string) => {
    return FIELD_TYPE_CONFIGS.find(c => c.type === type)?.label || type;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Custom Fields</h1>
            <p className="text-gray-600 mt-1">
              Manage custom fields for leads and other entities
            </p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Field
          </Button>
        </div>

        {/* Search and Stats */}
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search fields..."
              className="pl-9"
            />
          </div>
          <div className="text-sm text-gray-500">
            {fields.length} field{fields.length !== 1 ? 's' : ''} total
            {fields.filter(f => f.isActive).length !== fields.length && (
              <span className="ml-2">
                ({fields.filter(f => f.isActive).length} active)
              </span>
            )}
          </div>
        </div>

        {/* Fields List */}
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-6 h-6 bg-gray-200 rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-1/3" />
                    <div className="h-4 bg-gray-200 rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-red-50 rounded-lg">
            <p className="text-red-600">Failed to load custom fields</p>
          </div>
        ) : filteredFields.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No fields match your search' : 'No custom fields yet'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {searchQuery
                ? 'Try a different search term'
                : 'Create your first custom field to extend lead information'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Field
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-1"></div>
              <div className="col-span-3">Field Name</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Key</div>
              <div className="col-span-2">Options</div>
              <div className="col-span-2">Actions</div>
            </div>

            {/* Field Rows */}
            <div className="divide-y divide-gray-200">
              {filteredFields.map((field, index) => (
                <motion.div
                  key={field.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    'grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-gray-50 transition-colors',
                    draggedIndex === index && 'opacity-50',
                    !field.isActive && 'bg-gray-50 opacity-60'
                  )}
                >
                  {/* Drag Handle */}
                  <div className="col-span-1">
                    <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                  </div>

                  {/* Name */}
                  <div className="col-span-3">
                    <div className="font-medium text-gray-900">{field.name}</div>
                    {field.description && (
                      <div className="text-xs text-gray-500 truncate">
                        {field.description}
                      </div>
                    )}
                  </div>

                  {/* Type */}
                  <div className="col-span-2">
                    <Badge variant="secondary" className="text-xs">
                      {getFieldTypeLabel(field.fieldType)}
                    </Badge>
                  </div>

                  {/* Key */}
                  <div className="col-span-2">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {field.key}
                    </code>
                  </div>

                  {/* Options/Badges */}
                  <div className="col-span-2 flex items-center gap-1">
                    {field.validation?.required && (
                      <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                        Required
                      </span>
                    )}
                    {field.isSearchable && (
                      <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                        Search
                      </span>
                    )}
                    {field.isFilterable && (
                      <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                        Filter
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(field)}
                      title={field.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {field.isActive ? (
                        <Eye className="w-4 h-4" />
                      ) : (
                        <EyeOff className="w-4 h-4" />
                      )}
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingField(field)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(field)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Create Modal */}
        <CreateFieldModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={createField}
          isCreating={isCreating}
        />
      </div>
    </DashboardLayout>
  );
};

export default CustomFieldManager;
